import React, { Component } from 'react';
import { Button } from 'react-bootstrap';
import { checkIPFSAvailability, IPFS_GATEWAYS } from '../../utils/ipfs';
import './SystemStatus.css';

// 3-char max labels for the navbar button
const STATUS_BUTTON = {
  checking:    { text: '...', variant: 'outline-secondary' },
  online:      { text: ' \u25CF ', variant: 'outline-success' },
  warning:     { text: ' \u25CF ', variant: 'outline-warning' },
  readonly:    { text: ' \u25CF ', variant: 'outline-info' },
  limited:     { text: ' \u25CF ', variant: 'outline-secondary' },
  liveRender:  { text: ' \u25CF ', variant: 'outline-success' },
  archive:     { text: ' \u25CF ', variant: 'outline-info' },
  wizard:      { text: ' \u25CF ', variant: 'outline-warning' },
};

class SystemStatus extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ipfsStatus: null,
      checking: true,
      expanded: false,
      showToast: false,
      toastMessage: '',
    };
    this.wrapperRef = React.createRef();
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  async componentDidMount() {
    this._isMounted = true;
    this._ipfsChecked = false;
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    this._isMounted = false;
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside(event) {
    if (this.state.expanded && this.wrapperRef.current && !this.wrapperRef.current.contains(event.target)) {
      this.setState({ expanded: false });
    }
  }

  componentDidUpdate(prevProps) {
    // Only check IPFS once show becomes true (loading finished, sourceType is final)
    if (this.props.show && !this._ipfsChecked) {
      this._ipfsChecked = true;
      if (this.props.sourceType === 'blockchain') {
        this.checkStatus();
      } else {
        this.setState({ checking: false });
      }
    }
    if (!prevProps.ipfsUnavailable && this.props.ipfsUnavailable) {
      this.showToastMessage('IPFS unavailable - using live render');
    }
    // Worth interrupting for: these editions exist and are not being shown,
    // which otherwise looks like a smaller project rather than a failed read.
    const missing = this.props.missingEditions || 0;
    if (missing > 0 && missing !== (prevProps.missingEditions || 0)) {
      this.showToastMessage(
        `${missing} edition${missing === 1 ? '' : 's'} could not be loaded - mint hash unavailable`
      );
    }
  }

  showToastMessage(message) {
    this.setState({ showToast: true, toastMessage: message });
    setTimeout(() => {
      if (this._isMounted) this.setState({ showToast: false });
    }, 5000);
  }

  async checkStatus() {
    this.setState({ checking: true });
    try {
      const ipfsStatus = await checkIPFSAvailability(3000);
      if (this._isMounted) this.setState({ ipfsStatus, checking: false });
    } catch (error) {
      if (this._isMounted) this.setState({
        ipfsStatus: { available: false, gateway: null },
        checking: false
      });
    }
  }

  getStatusKey() {
    const { ipfsStatus, checking } = this.state;
    const { walletConnected, ipfsUnavailable, sourceType } = this.props;

    if (sourceType === 'archive') return 'archive';
    if (sourceType === 'wizard') return 'wizard';

    if (checking) return 'checking';
    if (ipfsUnavailable) return 'liveRender';

    const hasWallet = walletConnected;
    const hasIPFS = ipfsStatus?.available;

    if (hasWallet && hasIPFS) return 'online';
    if (hasWallet && !hasIPFS) return 'warning';
    if (!hasWallet && hasIPFS) return 'readonly';
    return 'limited';
  }

  getStatusLabel(key) {
    const labels = {
      checking: 'Checking',
      online: 'Online',
      warning: 'No IPFS',
      readonly: 'Read-only',
      limited: 'Limited',
      liveRender: 'Live Render',
      archive: 'Archive Mode',
      wizard: 'Wizard Mode',
    };
    return labels[key] || key;
  }

  toggleExpanded = (e) => {
    e.stopPropagation();
    this.setState(prevState => ({ expanded: !prevState.expanded }));
  }

  dismissToast = () => {
    this.setState({ showToast: false });
  }

  /**
   * Render the cache timestamp as an absolute date plus a relative age, so a
   * viewer can tell at a glance whether the snapshot they are looking at is
   * days or months behind the chain.
   */
  formatCacheDate(iso) {
    const then = new Date(iso);
    if (isNaN(then.getTime())) return null;

    const date = then.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

    // Count calendar days, not elapsed milliseconds. A snapshot taken yesterday
    // evening is less than 24 hours old but is not "today", and saying so next
    // to a date that already reads yesterday just looks broken.
    const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round((midnight(new Date()) - midnight(then)) / 86400000);

    let age;
    if (days <= 0) age = 'today';
    else if (days === 1) age = 'yesterday';
    else if (days < 31) age = `${days} days ago`;
    else if (days < 365) age = `${Math.floor(days / 30)} months ago`;
    else age = `${Math.floor(days / 365)}y ago`;

    return { date, age, stale: days >= 30 };
  }

  getSourceDescription(sourceType) {
    const descriptions = {
      blockchain: 'Reading live data from the Ethereum blockchain via your wallet.',
      archive: 'Showing a saved snapshot, not live chain data. Connect a wallet to read the chain directly.',
      wizard: 'Project preview mode with simulated data.',
    };
    return descriptions[sourceType] || '';
  }

  render() {
    const { ipfsStatus, checking, expanded, showToast, toastMessage } = this.state;
    const { walletConnected, networkName, show, ipfsUnavailable, sourceType, cacheGeneratedAt } = this.props;

    if (show === false) return null;

    const statusKey = this.getStatusKey();
    const btn = STATUS_BUTTON[statusKey];
    const isBlockchain = sourceType === 'blockchain';

    return (
      <>
        {showToast && (
          <div className="status-toast" onClick={this.dismissToast}>
            <span className="toast-icon">&#9888;</span>
            <span className="toast-message">{toastMessage}</span>
            <span className="toast-dismiss">&times;</span>
          </div>
        )}

        <span className="system-status-wrapper" ref={this.wrapperRef}>
          &nbsp;
          <Button
            className="justify-content-end font-display system-status-btn"
            variant={btn.variant}
            onClick={this.toggleExpanded}
            title={this.getStatusLabel(statusKey)}
          >
            {btn.text}
          </Button>

          {expanded && (
            <div className="status-panel">
              <div className="status-section">
                <strong>{this.getStatusLabel(statusKey)}</strong>
              </div>

              <div className="status-row">
                <span className="status-key">Source:</span>
                <span className="status-value">
                  {sourceType === 'blockchain' ? 'Blockchain' : sourceType === 'archive' ? 'Cached data' : sourceType === 'wizard' ? 'Simulated' : sourceType}
                </span>
              </div>

              {sourceType === 'archive' && (() => {
                const c = cacheGeneratedAt ? this.formatCacheDate(cacheGeneratedAt) : null;
                return (
                  <div className="status-row">
                    <span className="status-key">Snapshot:</span>
                    <span className={`status-value ${c && c.stale ? 'status-na' : ''}`}>
                      {c ? `${c.date} (${c.age})` : 'date unknown'}
                    </span>
                  </div>
                );
              })()}

              <div className="status-row">
                <span className="status-key">Access:</span>
                <span className={`status-value ${isBlockchain ? 'status-ok' : ''}`}>
                  {isBlockchain ? 'Read + Write' : 'Read-only'}
                </span>
              </div>

              <div className="status-row">
                <span className="status-key">Wallet:</span>
                <span className={`status-value ${walletConnected ? 'status-ok' : 'status-na'}`}>
                  {walletConnected ? 'Connected' : 'Not connected'}
                </span>
              </div>

              {networkName && isBlockchain && (
                <div className="status-row">
                  <span className="status-key">Network:</span>
                  <span className="status-value">{networkName}</span>
                </div>
              )}

              {isBlockchain && (
                <>
                  <div className="status-row">
                    <span className="status-key">IPFS:</span>
                    <span className={`status-value ${ipfsUnavailable ? 'status-na' : ipfsStatus?.available ? 'status-ok' : 'status-na'}`}>
                      {ipfsUnavailable
                        ? 'Offline (live render)'
                        : checking
                          ? 'Checking...'
                          : ipfsStatus?.available
                            ? ipfsStatus.gateway
                            : 'Unavailable'}
                    </span>
                  </div>

                  <div className="status-row">
                    <span className="status-key">Gateways:</span>
                    <span className="status-value status-small">
                      {IPFS_GATEWAYS.length} configured
                    </span>
                  </div>
                </>
              )}

              <div className="status-description">
                {this.getSourceDescription(sourceType)}
              </div>

              {isBlockchain && (
                <div className="status-footer">
                  <button
                    className="status-refresh-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      this.checkStatus();
                    }}
                  >
                    Refresh
                  </button>
                </div>
              )}
            </div>
          )}
        </span>
      </>
    );
  }
}

export default SystemStatus;
